/** Uniform random BigInt in [0, n) using the browser's secure source, with rejection sampling (no modulo bias). */
export function randomBelow(n: bigint, rand: (a: Uint32Array) => void = (a) => { crypto.getRandomValues(a); }): bigint {
  if (n <= BigInt(0)) throw new Error("n must be positive");
  if (n === BigInt(1)) return BigInt(0);
  const bits = n.toString(2).length;
  const words = Math.ceil(bits / 32);
  const mask = (BigInt(1) << BigInt(bits)) - BigInt(1);
  const buf = new Uint32Array(words);
  for (;;) {
    rand(buf);
    let x = BigInt(0);
    for (let i = 0; i < words; i++) x = (x << BigInt(32)) | BigInt(buf[i]);
    x &= mask;
    if (x < n) return x;
  }
}

export interface RandomOptions { min: bigint; max: bigint; count: number; unique: boolean }

const SHUFFLE_LIMIT = BigInt(100000);

/** Random integers in [min, max]. Unique picks never build the whole range unless it is small. */
export function randomIntegers({ min, max, count, unique }: RandomOptions, rand?: (a: Uint32Array) => void): bigint[] {
  const size = max - min + BigInt(1);
  if (!unique) return Array.from({ length: count }, () => min + randomBelow(size, rand));
  if (BigInt(count) > size) throw new Error(`Range only has ${size} possible unique values`);
  if (size <= SHUFFLE_LIMIT) {
    // Partial Fisher-Yates over a small pool.
    const pool = Array.from({ length: Number(size) }, (_, i) => min + BigInt(i));
    for (let i = 0; i < count; i++) {
      const j = i + Number(randomBelow(BigInt(pool.length - i), rand));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }
    return pool.slice(0, count);
  }
  // Large range: pick and retry on duplicates (count is at most 1000, so collisions are rare).
  const seen = new Set<string>();
  const out: bigint[] = [];
  while (out.length < count) {
    const v = min + randomBelow(size, rand);
    const k = v.toString();
    if (!seen.has(k)) { seen.add(k); out.push(v); }
  }
  return out;
}

/** Whole-number text only; says why otherwise. */
export function parseWhole(raw: string, label: string): { value: bigint } | { error: string } {
  const t = raw.trim().replace(/[,_\s]/g, "");
  if (!t) return { error: `Enter a ${label}.` };
  if (/^-?\d+[.]\d+$/.test(t)) return { error: `${label[0].toUpperCase()}${label.slice(1)} must be a whole number (no decimals).` };
  if (!/^-?\d+$/.test(t)) return { error: `${label[0].toUpperCase()}${label.slice(1)} must be a whole number.` };
  return { value: BigInt(t) };
}

export const MAX_RANDOM_COUNT = 10000;

export type RandomPlan =
  | { ok: true; min: bigint; max: bigint; count: number; swapped: boolean; capped: boolean }
  | { ok: false; error: string; field: "min" | "max" | "count" | "unique" };

/** Checks the three inputs together. Min above max is swapped (and reported), not refused. */
export function planRandom(minRaw: string, maxRaw: string, countRaw: string, unique: boolean): RandomPlan {
  const lo = parseWhole(minRaw, "minimum");
  if ("error" in lo) return { ok: false, error: lo.error, field: "min" };
  const hi = parseWhole(maxRaw, "maximum");
  if ("error" in hi) return { ok: false, error: hi.error, field: "max" };
  const ct = parseWhole(countRaw, "amount");
  if ("error" in ct) return { ok: false, error: ct.error, field: "count" };
  if (ct.value < BigInt(1)) return { ok: false, error: "How many must be at least 1.", field: "count" };
  const swapped = lo.value > hi.value;
  const min = swapped ? hi.value : lo.value, max = swapped ? lo.value : hi.value;
  const capped = ct.value > BigInt(MAX_RANDOM_COUNT);
  const count = Number(capped ? BigInt(MAX_RANDOM_COUNT) : ct.value);
  const size = max - min + BigInt(1);
  if (unique && BigInt(count) > size) {
    return { ok: false, error: `Only ${size} unique value${size === BigInt(1) ? "" : "s"} exist between ${min} and ${max}, so ${count} unique numbers is not possible. Lower "How many", widen the range or turn off "No duplicates".`, field: "unique" };
  }
  return { ok: true, min, max, count, swapped, capped };
}
